import { View, Text, Pressable, Image, Platform } from "react-native";
import {
  Heart,
  MapPin,
  Pencil,
  Shield,
  ShieldCheck,
  Video,
  Globe,
  Sparkles,
  DollarSign,
  Building2,
  Award,
} from "lucide-react-native";
import type { Organization } from "@louise/providers/providers.types";
import { CLINIC_SETTING_LABELS } from "@louise/providers/providers.types";
import {
  getClinicDecisionSupport,
  getOrganizationCountryLabel,
  type ClinicDecisionContext,
  type InsuranceMatchStatus,
} from "@louise/providers/hooks/useOrganizations";
import { EMBER } from "../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { getClinicFacilityOrLogoImages } from "./clinic/clinicImagePolicy";
import { useTranslations } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const clinicPlaceholder =
  require("../../assets/images/clinic-placeholder.jpg") as number;

type Props = {
  organization: Organization;
  onPress: (org: Organization) => void;
  distance?: string | null;
  insuranceMatch?: InsuranceMatchStatus;
  isSaved?: boolean;
  hasNotes?: boolean;
  onToggleSave?: (orgId: string) => void;
  homeCountry?: string;
  decisionContext?: ClinicDecisionContext;
};

function countryLabel(country: string) {
  return getOrganizationCountryLabel(country);
}

export default function MoonClinicCard({
  organization: org,
  onPress,
  distance,
  insuranceMatch,
  isSaved,
  hasNotes,
  onToggleSave,
  homeCountry,
  decisionContext,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MoonClinicCard");
  const cityState = [org.address.city, org.address.state]
    .filter(Boolean)
    .join(" · ");

  const heroImage = getClinicFacilityOrLogoImages(org.images)[0];
  const networkName = org.network?.name;
  const decision = getClinicDecisionSupport(org, decisionContext);
  const isInternational =
    !!homeCountry &&
    !!org.address.country &&
    org.address.country !== homeCountry;

  const nationalRank = org.outcomes?.nationalRank;
  const showTopBadge =
    org.address.country === "FR" && nationalRank != null && nationalRank <= 10;

  // Smart tags — show only compact decision info, max 4
  const tags: { label: string; color: string; icon?: string }[] = [];
  tags.push({
    label: `${decision.matchPercent}% match`,
    color: EMBER.colors.cryo,
    icon: "sparkles",
  });
  // Practice setting. Network clinics already surface their network name badge
  // over the hero image, so only boutique / hospital-based get a tag here.
  if (org.setting && org.setting !== "network-group") {
    tags.push({
      label: CLINIC_SETTING_LABELS[org.setting],
      color: EMBER.colors.textMid,
      icon: "building",
    });
  }
  if (isInternational) {
    tags.push({
      label: countryLabel(org.address.country),
      color: EMBER.colors.cryo,
      icon: "globe",
    });
  }
  if (insuranceMatch === "confirmed") {
    tags.push({
      label: t("access.clinic_card.your_insurance"),
      color: EMBER.colors.textMid,
      icon: "shield-check",
    });
  } else if (org.insurance?.accepted) {
    tags.push({
      label: t("access.clinic_card.insurance"),
      color: EMBER.colors.textMid,
      icon: "shield",
    });
  }
  const hasTelehealth = org.consultationTypes?.includes("telehealth");
  const hasInPerson = org.consultationTypes?.includes("in-person");
  if (hasTelehealth && hasInPerson) {
    tags.push({
      label: t("access.clinic_card.also_virtual"),
      color: EMBER.colors.textLight,
      icon: "video",
    });
  }
  if (decision.pricingLabel === "Price listed") {
    tags.push({
      label: "Price",
      color: EMBER.colors.textLight,
      icon: "dollar",
    });
  }
  if (org.isFirstConsultationFree) {
    tags.push({
      label: t("access.clinic_card.free_consult"),
      color: EMBER.colors.coral,
    });
  }

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(org.name), "moon_clinic_card");
        onPress(org);
      }}
      style={{
        ...(GLASS_CARD_STYLE as object),
        padding: 0,
        overflow: "hidden",
        flex: 1,
      }}
    >
      {/* Image / Placeholder */}
      <View
        style={{
          height: 90,
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Image
          source={heroImage ? { uri: heroImage.url } : clinicPlaceholder}
          style={{ width: "100%", height: "100%", position: "absolute" }}
          resizeMode="cover"
        />

        {/* Top-10 rank badge */}
        {showTopBadge && (
          <View
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              backgroundColor: "rgba(245, 158, 11, 0.85)",
              paddingHorizontal: 7,
              paddingVertical: 3,
              borderRadius: 8,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Award size={10} color="#FFFFFF" strokeWidth={2} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "700",
                color: "#FFFFFF",
                letterSpacing: 0.3,
              }}
            >
              {t("access.clinic_card.rank_badge", {
                params: { rank: String(nationalRank) },
              })}
            </Text>
          </View>
        )}

        {/* Save button */}
        {onToggleSave && (
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              // Heart icon only, with no visible copy to name it.
              capturePress(isSaved ? "unsave_clinic" : "save_clinic");
              onToggleSave(org.id);
            }}
            hitSlop={6}
            // Inert testability hooks: expose the save toggle as an
            // addressable button whose pressed state (aria-pressed) mirrors
            // isSaved, so E2E can assert the filled/unfilled heart. Purely
            // observational — does not affect behavior.
            accessibilityRole="button"
            aria-pressed={!!isSaved}
            testID="clinic-card-heart"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 26,
              height: 26,
              borderRadius: 13,
              backgroundColor: "rgba(0,0,0,0.3)",
              alignItems: "center",
              justifyContent: "center",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Heart
              size={13}
              color={isSaved ? EMBER.colors.coral : "#FFFFFF"}
              fill={isSaved ? EMBER.colors.coral : "transparent"}
              strokeWidth={2}
            />
          </Pressable>
        )}

        {/* Notes indicator */}
        {hasNotes && (
          <View
            style={{
              position: "absolute",
              top: 6,
              right: onToggleSave ? 38 : 6,
              width: 26,
              height: 26,
              borderRadius: 13,
              backgroundColor: "rgba(0,0,0,0.3)",
              alignItems: "center",
              justifyContent: "center",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Pencil size={11} color="#FFFFFF" strokeWidth={2} />
          </View>
        )}

        {/* Network badge */}
        {networkName && (
          <View
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              backgroundColor: "rgba(0,0,0,0.45)",
              paddingHorizontal: 7,
              paddingVertical: 3,
              borderRadius: 8,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 9,
                fontWeight: "600",
                color: "rgba(255,255,255,0.9)",
                letterSpacing: 0.3,
              }}
            >
              {networkName}
            </Text>
          </View>
        )}
      </View>

      {/* Content */}
      <View style={{ padding: 14, gap: 6, flex: 1 }}>
        {/* Name */}
        <Text
          className="font-dm-sans"
          testID="clinic-card-name"
          style={{
            fontSize: 14,
            fontWeight: "700",
            color: EMBER.colors.text,
            lineHeight: 18,
          }}
          numberOfLines={2}
        >
          {org.name}
        </Text>

        {/* Location + distance */}
        {cityState ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 3 }}>
            <MapPin
              size={10}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textLight }}
              numberOfLines={1}
            >
              {cityState}
              {distance ? ` · ${distance}` : ""}
            </Text>
          </View>
        ) : null}

        {/* Spacer to push tags to bottom */}
        <View style={{ flex: 1 }} />

        {/* Smart tags */}
        {tags.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 4,
              marginTop: 2,
            }}
          >
            {tags.slice(0, 4).map((tag) => (
              <View
                key={tag.label}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 3,
                  paddingHorizontal: 6,
                  paddingVertical: 3,
                  borderRadius: 6,
                  backgroundColor: `${tag.color}14`,
                }}
              >
                {tag.icon === "shield-check" && (
                  <ShieldCheck size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "shield" && (
                  <Shield size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "video" && (
                  <Video size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "globe" && (
                  <Globe size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "sparkles" && (
                  <Sparkles size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "dollar" && (
                  <DollarSign size={9} color={tag.color} strokeWidth={2} />
                )}
                {tag.icon === "building" && (
                  <Building2 size={9} color={tag.color} strokeWidth={2} />
                )}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 9,
                    fontWeight: "600",
                    color: tag.color,
                  }}
                  numberOfLines={1}
                >
                  {tag.label}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </Pressable>
  );
}
